import * as React from 'react';
import styled from 'styled-components';
import { getStoreState, storeDispatch } from 'state/Store';
import * as Actions from '../state/actions';
import { trackEvent } from 'utils/Tracker';
import { isPro } from 'utils';
import { ColorTheme } from 'data/ColorTheme';
import { styledWithProps } from '../utils';
import { hoverMixin } from 'utils/style';

const Wrapper = styled.div`
  text-align: left;
`;

const Subtitle = styled.div`
  opacity: 0.7;
  font-size: 14px;
  line-height: 20px;
`;

const ThemesRow = styled.div`
  display: flex;
  margin-top: 8px;

  & > * {
    margin-right: 4px;
  }

  button.plus {
    width: 60px;
    height: 60px;
    background-color: rgba(var(--primary-fg-rgb), 0.2);
    border-radius: 6px;
    ${hoverMixin('background-color: rgba(var(--primary-fg-rgb), 0.3);')}
  }
`;

const ColorThemeThumbWrapper = styledWithProps<{ theme: ColorTheme }>()(
  styled.div
)`
  width: 60px;
  height: 60px;
  border-radius: 6px;
  background-color: ${props => props.theme.background}
  position: relative;
  padding: 12px;
  border: 1px solid rgba(var(--menu-fg-rgb), 0.1);

  .foreground-color {
    background-color: ${props => props.theme.foreground}
    height: 100%;
    border-radius: 50%;
  }

  .accent-color {
    background-color: ${props => props.theme.accent}
    position: absolute;
    width: 9px;
    height: 9px;
    right: 5.5px;
    bottom: 5.5px;
    border-radius: 50%;
  }

  .remove {
    position: absolute;
    background-color: var(--bright-red);
    border: 1px solid rgba(var(--menu-fg-rgb), 0.1);
    width: 16px;
    height: 16px;
    top: -8px;
    right: -4px;
    border-radius: 50%;
    cursor: pointer;
    ${hoverMixin('transform: scale(1.15)')}

    &:after {
      content: '';
      position: absolute;
      display: block;
      top: 6px;
      left: 3px;
      width: 8px;
      height: 1.5px;
      background-color: white;
    }
  }
`;

const ColorThemeThumb: React.FC<{
  theme: ColorTheme;
  onRemove: () => void;
}> = ({ theme, onRemove }) => {
  return (
    <ColorThemeThumbWrapper theme={theme}>
      <div className="foreground-color" />
      <div className="accent-color" />
      <div className="remove" onClick={onRemove} />
    </ColorThemeThumbWrapper>
  );
};

export const ColorThemesRow: React.FC = () => {
  const showUpgradeModal = React.useCallback(() => {
    trackEvent('Clicked Upgrade', { location: 'Color themes settings' });
    storeDispatch(Actions.showBuyingOptions());
  }, []);

  const addColorTheme = React.useCallback(() => {
    storeDispatch(Actions.showAddColorTheme());
  }, []);

  const removeColorTheme = React.useCallback((index: number) => {
    storeDispatch(Actions.removeColorTheme(index));
  }, []);

  const user = getStoreState().app.user;
  const pro = isPro(user);
  const themes = user?.colorThemes ?? [];

  return (
    <Wrapper>
      <div>Color themes</div>
      <Subtitle>
        Quickly apply your brand colors to all the templates that support this.{' '}
        {!pro && (
          <>
            Only available to Pro users -{' '}
            <a onClick={showUpgradeModal}>Upgrade</a>
          </>
        )}
      </Subtitle>
      {pro && (
        <ThemesRow>
          {themes.length > 0 &&
            themes.map((c, index) => (
              <ColorThemeThumb
                theme={c}
                key={`${c.accent}-${c.background}-${c.foreground}-${index}`}
                onRemove={() => removeColorTheme(index)}
              />
            ))}
          <button className="icon plus" onClick={addColorTheme} />
        </ThemesRow>
      )}
    </Wrapper>
  );
};



// WEBPACK FOOTER //
// ./src/account/ColorThemesRow.tsx